/** Choose: the caption palette the server solved for the focused template on this clip — from its latest tweak
 * preview (GET /api/clips/{id}/tries?template_id=…, the same query the Edit drawer polls): swatches with their scores,
 * the black-stroke flag, the winning brand mix, the reason and the seed. */
import { useQuery } from "@tanstack/react-query";
import { qk } from "../../api/keys";
import type { TemplateOut, TryOut } from "../types";
import { listTries } from "./api";
import { asPalette, cell, type PaletteOut } from "./clipLanes";

const POLL_MS = 2000;  // = TemplateEditor.POLL_MS

/** The latest try of a template on a clip (shared with TemplateEditor's query key). */
export function useLatestTry(clipId: number, templateId: string | null) {
  return useQuery({
    queryKey: qk.list("tries", { clipId, template: templateId ?? "" }),
    queryFn: () => listTries(clipId, templateId as string),
    enabled: templateId != null,
    refetchInterval: (q) => {
      const t = (q.state.data ?? [])[0];
      return t && (t.status === "queued" || t.status === "running") ? POLL_MS : false;
    },
  });
}

export function latestOf(tries: readonly TryOut[] | undefined): TryOut | null {
  return tries && tries.length ? [...tries].sort((a, b) => b.id - a.id)[0] ?? null : null;
}

/** Per-colour scores (`scores[hex]` as {metric: value}) and the remaining score entries. */
export function paletteScores(p: PaletteOut): { perColour: Record<string, string>; other: [string, string][] } {
  const perColour: Record<string, string> = {};
  const used = new Set<string>();
  for (const c of p.colours) {
    const v = p.scores[c] ?? p.scores[c.toLowerCase()] ?? p.scores[c.toUpperCase()];
    if (v == null) continue;
    used.add(p.scores[c] != null ? c : p.scores[c.toLowerCase()] != null ? c.toLowerCase() : c.toUpperCase());
    perColour[c] = v && typeof v === "object" && !Array.isArray(v)
      ? Object.entries(v as Record<string, unknown>).map(([k, x]) => `${k} ${cell(x)}`).join(" · ") : cell(v);
  }
  const other = Object.entries(p.scores).filter(([k]) => !used.has(k)).map(([k, v]) => [k, cell(v)] as [string, string]);
  return { perColour, other };
}

export function PaletteView({ p, seed }: { p: PaletteOut; seed?: number | null }) {
  const sc = paletteScores(p);
  return (
    <div data-testid="palette-view">
      <ul className="ws8-swatches" aria-label="Caption colours in paint order">
        {p.colours.map((c, i) => (
          <li key={`${c}-${i}`} data-testid="palette-swatch">
            <span className={`ws8-swatch ws8-swatch-lg${p.stroke ? " ws8-swatch-stroke" : ""}`} aria-hidden="true"
              style={{ background: c }} />
            <span>{c}</span>
            {sc.perColour[c] && <span className="ws8-muted"> {sc.perColour[c]}</span>}
          </li>
        ))}
      </ul>
      <p>
        Black stroke: <strong data-testid="palette-stroke">{p.stroke ? "on" : "off"}</strong>
        {" · "}mix <strong data-testid="palette-mix">{p.mix_id || "explicit palette"}</strong>
        {seed != null ? <> · seed <span data-testid="palette-seed">{seed}</span></> : null}
        {p.version ? <span className="ws8-muted"> ({p.version})</span> : null}
      </p>
      {p.reason && <p className="ws8-muted" data-testid="palette-reason">{p.reason}</p>}
      {sc.other.length > 0 && (
        <p className="ws8-muted">Scores: {sc.other.map(([k, v]) => `${k} ${v}`).join(" · ")}</p>
      )}
    </div>
  );
}

export function PaletteCard({ clipId, template }: { clipId: number; template: TemplateOut | null }) {
  const q = useLatestTry(clipId, template?.id ?? null);
  if (!template) return null;
  const t = latestOf(q.data);
  const p = t ? asPalette(t.palette) : null;
  return (
    <section className="ws8-panel" aria-labelledby="ws8-palette-h" data-testid="palette-card">
      <h3 id="ws8-palette-h" className="ws8-h2">Caption palette: {template.title}</h3>
      {!t ? (
        <p className="ws8-muted">No preview of {template.title} on this clip yet. Preview (or Reroll) in its editor solves the palette.</p>
      ) : p ? (
        <PaletteView p={p} seed={t.seed} />
      ) : (
        <p className="ws8-muted" data-testid="palette-none">
          Preview #{t.id} ({t.status}) carries no palette solution{t.seed != null ? ` (seed ${t.seed})` : ""}.
        </p>
      )}
    </section>
  );
}
